// Idle-timeout hook: auto-logs the user out after `timeoutMs` of no activity.
// Shows a warning countdown (default 30s) that the user can dismiss with any click.
//
// Activity signals: mousemove, mousedown, keydown, wheel, touchstart, scroll.
// The timer uses `Date.now()` + a lightweight rAF-independent setTimeout so it survives
// tab throttling reasonably well.
import { useEffect, useRef, useState, useCallback } from "react";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];

export default function useIdleTimeout({ timeoutMs, warningMs = 30000, onTimeout, enabled = true }) {
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(0);
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const clearAll = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    idleTimerRef.current = null;
    warnTimerRef.current = null;
    tickTimerRef.current = null;
  }, []);

  const startTimers = useCallback(() => {
    clearAll();
    setWarningSecondsLeft(0);
    lastActivityRef.current = Date.now();
    // Fire warning at (timeout - warning) ms from now
    const warnDelay = Math.max(0, timeoutMs - warningMs);
    warnTimerRef.current = setTimeout(() => {
      setWarningSecondsLeft(Math.ceil(warningMs / 1000));
      tickTimerRef.current = setInterval(() => {
        setWarningSecondsLeft((s) => (s > 1 ? s - 1 : 0));
      }, 1000);
    }, warnDelay);
    // Actual logout at full timeout
    idleTimerRef.current = setTimeout(() => {
      clearAll();
      setWarningSecondsLeft(0);
      onTimeout?.();
    }, timeoutMs);
  }, [timeoutMs, warningMs, onTimeout, clearAll]);

  const dismissWarning = useCallback(() => {
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!enabled) {
      clearAll();
      setWarningSecondsLeft(0);
      return undefined;
    }
    startTimers();
    // Any activity resets timers — but during the warning window we DO NOT auto-reset:
    // the user must explicitly click "أنا موجود" so we know they're actually there.
    const onActivity = () => {
      if (warningSecondsLeft > 0) return;
      // Throttle: only reset if >2s since last activity
      const now = Date.now();
      if (now - lastActivityRef.current < 2000) return;
      startTimers();
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, timeoutMs, warningMs]);

  return { warningSecondsLeft, dismissWarning };
}
